import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kognitos/lattice';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';
import { useActivateTrigger } from '@/generated/hooks/grimoire/v1/automation_phase';
import { captureException } from '@/modules/sentry';
import {
  TRIGGER_TYPE,
  type TTriggerType,
} from '../../utils/trigger-status-utils';

/**
 * Props for the ResumeTriggerDialog component.
 */
export interface IResumeTriggerDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The type of trigger being resumed */
  triggerType: TTriggerType;
  /** The organization ID */
  organizationId: string;
  /** The workspace ID */
  workspaceId: string;
  /** The automation ID */
  automationId: string;
  /** The trigger ID to resume */
  triggerId: string;
  /** Whether the automation is a draft */
  isDraft?: boolean;
  /** Callback when resume is successful */
  onSuccess?: () => void;
}

/**
 * A confirmation dialog for resuming triggers.
 *
 * Displays trigger-type-specific messaging (webhook vs email) and handles
 * the resume mutation with loading states, toast notifications, and error handling.
 *
 * @param {IResumeTriggerDialogProps} props - Component props
 * @returns {JSX.Element} The resume trigger confirmation dialog
 *
 * @example
 * <ResumeTriggerDialog
 *   isOpen={isResumeDialogOpen}
 *   onClose={closeResumeDialog}
 *   triggerType="webhook"
 *   organizationId={orgId}
 *   workspaceId={wsId}
 *   automationId={autoId}
 *   triggerId={triggerId}
 *   onSuccess={handleResumeSuccess}
 * />
 */
export function ResumeTriggerDialog({
  isOpen,
  onClose,
  triggerType,
  organizationId,
  workspaceId,
  automationId,
  triggerId,
  isDraft = false,
  onSuccess,
}: IResumeTriggerDialogProps) {
  const { t } = useTranslation('automation-inputs');

  const { mutate: resumeTrigger, isPending } = useActivateTrigger({
    mutation: {
      onSuccess: () => {
        toast.success(t('trigger.toast.resumeSuccess'));
        onClose();
        onSuccess?.();
      },
      onError: error => {
        toast.error(t('trigger.toast.resumeError'));
        captureException(error, {
          tags: {
            component: 'ResumeTriggerDialog',
            action: 'resumeTrigger',
          },
        });
      },
    },
  });

  /**
   * Handles the resume trigger action when user confirms.
   */
  const handleResumeTrigger = () => {
    resumeTrigger({
      organization: organizationId,
      workspace: workspaceId,
      automation: automationId,
      triggerId,
      data: {
        stage: isDraft
          ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
          : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
      },
    });
  };

  const titleKey =
    triggerType === TRIGGER_TYPE.WEBHOOK
      ? 'resumeDialog.webhookTitle'
      : triggerType === TRIGGER_TYPE.EMAIL
        ? 'resumeDialog.emailTitle'
        : 'resumeDialog.pollTitle';
  const descriptionKey =
    triggerType === TRIGGER_TYPE.WEBHOOK
      ? 'resumeDialog.webhookDescription'
      : triggerType === TRIGGER_TYPE.EMAIL
        ? 'resumeDialog.emailDescription'
        : 'resumeDialog.pollDescription';

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription className="flex flex-col">
            <span>{t(descriptionKey)}</span>
            <span>{t('resumeDialog.continueNote')}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('resumeDialog.cancel')}
          </Button>
          <Button
            onClick={handleResumeTrigger}
            disabled={isPending}
            isLoading={isPending}
          >
            {t('resumeDialog.resumeTrigger')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
